/**
 * AI Settings Page
 * Allows users to configure their LLM API providers for Smart Analysis features
 * with intelligent routing based on data sensitivity and Swiss compliance requirements
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Brain,
  Key,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Shield,
  ShieldCheck,
  ExternalLink,
  Lock,
  Eye,
  Mic,
  Globe,
  MapPin,
  Star,
  Settings2,
} from "lucide-react";

// FINMA classification badge component
const FINMA_BADGE_CONFIG = {
  finma_required: {
    label: "FINMA Required",
    className: "bg-destructive/10 text-destructive",
    icon: ShieldCheck,
  },
  finma_recommended: {
    label: "FINMA Recommended",
    className: "bg-warning/15 text-warning",
    icon: Shield,
  },
  finma_optional: {
    label: "Optional",
    className: "bg-muted text-muted-foreground",
    icon: null,
  },
} as const;

function FinmaBadge({
  classification,
}: {
  classification: keyof typeof FINMA_BADGE_CONFIG;
}) {
  const config = FINMA_BADGE_CONFIG[classification];
  const Icon = config.icon;
  return (
    <Badge className={`text-xs gap-1 ${config.className}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {config.label}
    </Badge>
  );
}

// Provider display info with residency hints
const PROVIDER_INFO: Record<
  string,
  {
    name: string;
    description: string;
    docsUrl?: string;
    swissAvailable?: boolean;
    isBuiltIn?: boolean;
    isSubscription?: boolean;
    models?: Array<{ id: string; name: string }>;
  }
> = {
  // Built-in provider (no API key needed)
  "vertex-builtin": {
    name: "🇨🇭 Built-in Gemini (Swiss)",
    description: "Vertex AI in Zurich - No API key needed",
    swissAvailable: true,
    isBuiltIn: true,
    models: [
      { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash" },
      { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro (Most Capable)" },
      { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash (Balanced)" },
      { id: "gemini-1.5-flash-8b", name: "Gemini 1.5 Flash-8B (Fast)" },
    ],
  },
  // Claude subscription (uses OAuth, no API key needed)
  "claude-subscription": {
    name: "🔐 Claude Subscription",
    description: "Use your Claude Pro/Team subscription - OAuth login",
    isSubscription: true,
    models: [
      { id: "claude-3-5-sonnet-20241022", name: "Claude 3.5 Sonnet (Latest)" },
      { id: "claude-3-5-haiku-20241022", name: "Claude 3.5 Haiku (Fast)" },
      { id: "claude-3-opus-20240229", name: "Claude 3 Opus (Most Capable)" },
    ],
  },
  // User API key providers
  anthropic: {
    name: "Anthropic API",
    description: "Claude 3.5/3 via API key",
    docsUrl: "https://console.anthropic.com/settings/keys",
    models: [
      {
        id: "claude-3-5-sonnet-20241022",
        name: "Claude 3.5 Sonnet (Recommended)",
      },
      { id: "claude-3-5-haiku-20241022", name: "Claude 3.5 Haiku (Fast)" },
      { id: "claude-3-opus-20240229", name: "Claude 3 Opus (Most Capable)" },
      { id: "claude-3-sonnet-20240229", name: "Claude 3 Sonnet" },
      { id: "claude-3-haiku-20240307", name: "Claude 3 Haiku (Budget)" },
    ],
  },
  openai: {
    name: "OpenAI",
    description: "GPT-4o, o1/o3 reasoning models",
    docsUrl: "https://platform.openai.com/api-keys",
    models: [
      { id: "gpt-4o", name: "GPT-4o (Latest)" },
      { id: "gpt-4o-mini", name: "GPT-4o Mini (Fast)" },
      { id: "o1", name: "o1 (Deep Reasoning)" },
      { id: "o1-mini", name: "o1-mini (Fast Reasoning)" },
      { id: "o3-mini", name: "o3-mini (Latest Reasoning)" },
      { id: "gpt-4-turbo", name: "GPT-4 Turbo (128k)" },
    ],
  },
  gemini: {
    name: "Google Gemini (API Key)",
    description: "Your own Gemini API key",
    docsUrl: "https://aistudio.google.com/apikey",
    models: [
      { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash" },
      { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro (Most Capable)" },
      { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash (Balanced)" },
      { id: "gemini-1.5-flash-8b", name: "Gemini 1.5 Flash-8B (Fast)" },
    ],
  },
  // z.ai - pay-per-token service with coding focus
  zai: {
    name: "z.ai",
    description: "Pay-per-token API with coding-focused models",
    docsUrl: "https://z.ai/account",
    models: [
      { id: "zai-coder-v1", name: "z.ai Coder v1 (Code Analysis)" },
      { id: "zai-analyst-v1", name: "z.ai Analyst v1 (Data Analysis)" },
    ],
  },
  groq: {
    name: "Groq",
    description: "Llama 3.3, Mixtral (ultra-fast inference)",
    docsUrl: "https://console.groq.com/keys",
    models: [
      { id: "llama-3.3-70b-versatile", name: "Llama 3.3 70B (Versatile)" },
      { id: "llama-3.1-70b-versatile", name: "Llama 3.1 70B" },
      { id: "llama-3.1-8b-instant", name: "Llama 3.1 8B (Instant)" },
      { id: "mixtral-8x7b-32768", name: "Mixtral 8x7B (32k)" },
    ],
  },
  openrouter: {
    name: "OpenRouter",
    description: "Multi-provider unified API",
    docsUrl: "https://openrouter.ai/keys",
    models: [
      { id: "anthropic/claude-3.5-sonnet", name: "Claude 3.5 Sonnet" },
      { id: "openai/gpt-4o", name: "GPT-4o" },
      { id: "google/gemini-pro-1.5", name: "Gemini 1.5 Pro" },
      { id: "meta-llama/llama-3.1-405b-instruct", name: "Llama 3.1 405B" },
    ],
  },
  azure: {
    name: "Azure OpenAI",
    description: "Enterprise Azure deployment with Swiss regions",
    swissAvailable: true,
    docsUrl: "https://portal.azure.com",
    models: [
      { id: "gpt-4o", name: "GPT-4o (Azure)" },
      { id: "gpt-4", name: "GPT-4 (Azure)" },
      { id: "gpt-35-turbo", name: "GPT-3.5 Turbo (Azure)" },
    ],
  },
  bedrock: {
    name: "AWS Bedrock",
    description: "Amazon Bedrock with Claude, Llama, Titan",
    docsUrl: "https://console.aws.amazon.com/bedrock",
    models: [
      {
        id: "anthropic.claude-3-5-sonnet-20241022-v2:0",
        name: "Claude 3.5 Sonnet",
      },
      { id: "anthropic.claude-3-haiku-20240307-v1:0", name: "Claude 3 Haiku" },
      { id: "meta.llama3-1-70b-instruct-v1:0", name: "Llama 3.1 70B" },
      { id: "amazon.titan-text-premier-v1:0", name: "Amazon Titan Premier" },
    ],
  },
  "google-vertex": {
    name: "Google Vertex AI",
    description: "Enterprise Google Cloud with Swiss regions",
    swissAvailable: true,
    docsUrl: "https://console.cloud.google.com/vertex-ai",
    models: [
      { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash" },
      { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro" },
      { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash" },
    ],
  },
  xai: {
    name: "xAI",
    description: "Grok models from xAI",
    docsUrl: "https://x.ai",
    models: [
      { id: "grok-2", name: "Grok 2" },
      { id: "grok-2-mini", name: "Grok 2 Mini" },
    ],
  },
  cerebras: {
    name: "Cerebras",
    description: "Ultra-fast inference for Llama models",
    docsUrl: "https://cloud.cerebras.ai",
    models: [
      { id: "llama3.1-70b", name: "Llama 3.1 70B (Cerebras)" },
      { id: "llama3.1-8b", name: "Llama 3.1 8B (Cerebras)" },
    ],
  },
  huggingface: {
    name: "Hugging Face",
    description: "Open source models via Inference API",
    docsUrl: "https://huggingface.co/settings/tokens",
    models: [
      { id: "mistralai/Mixtral-8x7B-Instruct-v0.1", name: "Mixtral 8x7B" },
      { id: "meta-llama/Meta-Llama-3.1-70B-Instruct", name: "Llama 3.1 70B" },
      { id: "Qwen/Qwen2.5-72B-Instruct", name: "Qwen 2.5 72B" },
    ],
  },
};

// Usage category metadata
const CATEGORY_INFO: Record<
  string,
  { name: string; description: string; icon: React.ReactNode; color: string }
> = {
  private_data: {
    name: "Private Data",
    description:
      "Bank statements, financial data - requires Swiss/EU residency",
    icon: <Lock className="h-4 w-4" />,
    color: "text-warning",
  },
  multimodal: {
    name: "Multi-modal",
    description: "Document analysis, OCR, image processing",
    icon: <Eye className="h-4 w-4" />,
    color: "text-info",
  },
  voice: {
    name: "Voice",
    description: "Speech-to-text, voice consulting features",
    icon: <Mic className="h-4 w-4" />,
    color: "text-[var(--primary)]",
  },
  general: {
    name: "General",
    description: "Non-sensitive operations, general AI features",
    icon: <Globe className="h-4 w-4" />,
    color: "text-success",
  },
};

// Data residency badges - using semantic token colors
const RESIDENCY_INFO: Record<
  string,
  { name: string; flag: string; color: string }
> = {
  swiss: {
    name: "Swiss",
    flag: "🇨🇭",
    color: "bg-destructive/10 text-destructive",
  },
  eu: { name: "EU", flag: "🇪🇺", color: "bg-primary/10 text-primary" },
  global: {
    name: "Global",
    flag: "🌍",
    color: "bg-muted text-muted-foreground",
  },
};

interface ProviderFormData {
  id?: number;
  name: string;
  providerId: string;
  providerType: string;
  apiKey: string;
  apiEndpoint?: string;
  selectedModel?: string;
  usageCategory: "private_data" | "multimodal" | "voice" | "general";
  dataResidency: "swiss" | "eu" | "global";
  isDefaultForCategory: boolean;
  customInstructions?: string;
  capabilities: {
    vision?: boolean;
    audio?: boolean;
    code?: boolean;
    longContext?: boolean;
    streaming?: boolean;
    functionCalling?: boolean;
    embedding?: boolean;
  };
}

export default function AISettings() {
  return (
    <DashboardLayout>
      <AISettingsContent />
    </DashboardLayout>
  );
}

function AISettingsContent() {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  // State
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProvider, setEditingProvider] =
    useState<ProviderFormData | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProviderFormData>({
    name: "",
    providerId: "openai",
    providerType: "openai",
    apiKey: "",
    usageCategory: "general",
    dataResidency: "global",
    isDefaultForCategory: false,
    customInstructions: "",
    capabilities: {},
  });
  const [activeTab, setActiveTab] = useState<string>("all");

  // Queries
  const { data: providers, isLoading } = trpc.aiSettings.list.useQuery();
  const { data: constants } = trpc.aiSettings.getConstants.useQuery();

  // Mutations
  const saveMutation = trpc.aiSettings.save.useMutation({
    onSuccess: () => {
      utils.aiSettings.list.invalidate();
      setShowAddDialog(false);
      setEditingProvider(null);
      resetForm();
      toast.success(
        editingProvider ? "Provider updated" : "Provider added successfully"
      );
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.aiSettings.delete.useMutation({
    onSuccess: () => {
      utils.aiSettings.list.invalidate();
      setDeleteConfirm(null);
      toast.success("Provider removed");
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      providerId: "vertex-builtin",
      providerType: "google-vertex",
      apiKey: "",
      selectedModel: "gemini-2.5-flash",
      usageCategory: "general",
      dataResidency: "swiss",
      isDefaultForCategory: false,
      customInstructions: "",
      capabilities: {},
    });
  };

  // Check if the selected provider is built-in (no API key required)
  const isBuiltInProvider =
    PROVIDER_INFO[formData.providerId]?.isBuiltIn === true;

  // Check if the selected provider uses subscription auth (OAuth)
  const isSubscriptionProvider =
    PROVIDER_INFO[formData.providerId]?.isSubscription === true;

  // API key not required for built-in or subscription providers
  const requiresApiKey = !isBuiltInProvider && !isSubscriptionProvider;

  // Get available models for the selected provider
  const availableModels = PROVIDER_INFO[formData.providerId]?.models || [];

  // Type for providers returned from the API
  type ApiProvider = NonNullable<typeof providers>[number];

  const handleEdit = (provider: ApiProvider) => {
    const editData: ProviderFormData = {
      id: provider.id,
      name: provider.name,
      providerId: provider.providerId,
      providerType: provider.providerType,
      apiKey: "", // Never pre-fill the key
      usageCategory: (provider.usageCategory ||
        "general") as ProviderFormData["usageCategory"],
      dataResidency: (provider.dataResidency ||
        "global") as ProviderFormData["dataResidency"],
      isDefaultForCategory: provider.isDefaultForCategory ?? false,
      customInstructions: provider.customInstructions ?? "",
      capabilities:
        (provider.capabilities as ProviderFormData["capabilities"]) ?? {},
    };
    setEditingProvider(editData);
    setFormData(editData);
    setShowAddDialog(true);
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a name for this provider");
      return;
    }

    // API key is only required for providers that aren't built-in or subscription-based
    if (!editingProvider && requiresApiKey && !formData.apiKey.trim()) {
      toast.error("Please enter your API key");
      return;
    }

    // Map special providers to backend-compatible IDs
    let actualProviderId = formData.providerId;
    if (formData.providerId === "vertex-builtin") {
      actualProviderId = "google-vertex";
    } else if (formData.providerId === "claude-subscription") {
      actualProviderId = "anthropic";
    }

    // Determine the API key value
    let apiKeyValue: string | undefined;
    if (isBuiltInProvider) {
      apiKeyValue = "builtin";
    } else if (isSubscriptionProvider) {
      apiKeyValue = "subscription"; // Will trigger OAuth flow on use
    } else {
      apiKeyValue = formData.apiKey || undefined;
    }

    saveMutation.mutate({
      id: formData.id,
      name: formData.name,
      providerId: actualProviderId as any,
      providerType: formData.providerType as any,
      apiKey: apiKeyValue,
      apiEndpoint:
        isBuiltInProvider || isSubscriptionProvider
          ? undefined
          : formData.apiEndpoint,
      usageCategory: formData.usageCategory,
      dataResidency: formData.dataResidency,
      isDefaultForCategory: formData.isDefaultForCategory,
      customInstructions: formData.customInstructions || undefined,
      capabilities:
        Object.keys(formData.capabilities).length > 0
          ? formData.capabilities
          : undefined,
      // Include selected model in config
      defaultLargeModelId: formData.selectedModel,
    });
  };

  // Filter providers by category
  const filteredProviders = providers?.filter(
    p => activeTab === "all" || p.usageCategory === activeTab
  );

  const getProviderInfo = (providerId: string) => {
    return (
      PROVIDER_INFO[providerId] || {
        name: providerId,
        description: "Custom provider",
      }
    );
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">
          Please log in to manage AI settings
        </p>
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Brain className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">AI Settings</h1>
          <p className="text-muted-foreground">
            Configure your LLM providers for Smart Analysis features
          </p>
        </div>
      </div>

      {/* Security Notice */}
      <Card className="mb-6 border-[var(--info)]/30 bg-info/5">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Shield className="h-5 w-5 text-info flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium text-sm">Your API keys are encrypted</p>
              <p className="text-sm text-muted-foreground">
                All API keys are encrypted using AES-256-GCM before storage.
                Keys are never logged or exposed in responses.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider List with Category Tabs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Configured Providers</CardTitle>
            <CardDescription>
              Configure providers for different use cases with appropriate data
              residency
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setEditingProvider(null);
              setShowAddDialog(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Provider
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="all" className="text-xs">
                All
              </TabsTrigger>
              <TabsTrigger value="private_data" className="text-xs">
                <Lock className="h-3 w-3 mr-1" />
                Private
              </TabsTrigger>
              <TabsTrigger value="multimodal" className="text-xs">
                <Eye className="h-3 w-3 mr-1" />
                Vision
              </TabsTrigger>
              <TabsTrigger value="voice" className="text-xs">
                <Mic className="h-3 w-3 mr-1" />
                Voice
              </TabsTrigger>
              <TabsTrigger value="general" className="text-xs">
                <Globe className="h-3 w-3 mr-1" />
                General
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredProviders && filteredProviders.length > 0 ? (
                <div className="space-y-4">
                  {filteredProviders.map(provider => {
                    const info = getProviderInfo(provider.providerId);
                    const categoryInfo = CATEGORY_INFO[provider.usageCategory];
                    const residencyInfo =
                      RESIDENCY_INFO[provider.dataResidency];
                    return (
                      <div
                        key={provider.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:border-primary/30 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center ${categoryInfo?.color || ""}`}
                          >
                            {categoryInfo?.icon || <Key className="h-5 w-5" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">
                                {provider.name}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {info.name}
                              </Badge>
                              {provider.isDefaultForCategory && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Star className="h-4 w-4 text-warning fill-[var(--warning)]" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Default for{" "}
                                    {categoryInfo?.name || "this category"}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {provider.isActive && (
                                <CheckCircle2 className="h-4 w-4 text-success" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                                {provider.maskedKey}
                              </code>
                              {residencyInfo && (
                                <Badge
                                  className={`text-xs ${residencyInfo.color}`}
                                >
                                  {residencyInfo.flag} {residencyInfo.name}
                                </Badge>
                              )}
                              {categoryInfo && (
                                <Badge variant="outline" className="text-xs">
                                  {categoryInfo.name}
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                Updated{" "}
                                {new Date(
                                  provider.updatedAt
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(provider)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirm(provider.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium mb-2">
                    {activeTab === "all"
                      ? "No providers configured"
                      : `No ${CATEGORY_INFO[activeTab]?.name || activeTab} providers`}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {activeTab === "private_data"
                      ? "Add a Swiss or EU provider for sensitive financial data"
                      : "Add an API key to enable AI-powered features"}
                  </p>
                  <Button
                    onClick={() => {
                      resetForm();
                      if (activeTab !== "all") {
                        setFormData(prev => ({
                          ...prev,
                          usageCategory:
                            activeTab as ProviderFormData["usageCategory"],
                          dataResidency:
                            activeTab === "private_data" ? "swiss" : "global",
                        }));
                      }
                      setShowAddDialog(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add{" "}
                    {activeTab === "all"
                      ? "Your First"
                      : CATEGORY_INFO[activeTab]?.name}{" "}
                    Provider
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Data Residency Guide */}
      <Card className="mt-6 border-[var(--warning)]/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Swiss Data Compliance
          </CardTitle>
          <CardDescription>
            For bank statements and financial data, use providers with Swiss or
            EU data residency
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {Object.entries(RESIDENCY_INFO).map(([key, info]) => (
              <div
                key={key}
                className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
              >
                <span className="text-xl">{info.flag}</span>
                <div className="flex-1">
                  <p className="font-medium text-sm">{info.name} Residency</p>
                  <p className="text-xs text-muted-foreground">
                    {key === "swiss" &&
                      "Data stays in Switzerland - required for sensitive bank data"}
                    {key === "eu" &&
                      "Data stays in EU - acceptable for most financial operations"}
                    {key === "global" &&
                      "Data may be processed globally - use for non-sensitive operations"}
                  </p>
                </div>
                {key === "swiss" && <Lock className="h-4 w-4 text-warning" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* FINMA Compliance Info */}
      <Card className="mt-6 border-[var(--info)]/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            FINMA Compliance
          </CardTitle>
          <CardDescription>
            AI processes are classified by their FINMA regulatory requirements.
            Compliance is available, not forced — you choose your posture but
            are always informed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <FinmaBadge classification="finma_required" />
              <p className="text-xs text-muted-foreground flex-1">
                Processes handling core financial data (VAT, tax, bank
                transactions). Swiss data residency is required.
              </p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <FinmaBadge classification="finma_recommended" />
              <p className="text-xs text-muted-foreground flex-1">
                Processes handling business-sensitive data (vendors,
                compliance). Swiss/EU residency is recommended.
              </p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <FinmaBadge classification="finma_optional" />
              <p className="text-xs text-muted-foreground flex-1">
                Non-sensitive operations (format detection, charts, learning).
                No residency requirements.
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t">
            <a
              href="https://www.finma.ch/en/supervision/outsourcing/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary flex items-center gap-1 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              FINMA Outsourcing Guidelines
            </a>
          </div>
        </CardContent>
      </Card>

      {/* AI Process Categories */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI-Powered Features
          </CardTitle>
          <CardDescription>
            These features use AI to automate financial operations. Each
            category can use different models.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="accounting" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="document" className="text-xs">
                <Eye className="h-3 w-3 mr-1" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="accounting" className="text-xs">
                <Lock className="h-3 w-3 mr-1" />
                Accounting
              </TabsTrigger>
              <TabsTrigger value="analysis" className="text-xs">
                <Brain className="h-3 w-3 mr-1" />
                Analysis
              </TabsTrigger>
              <TabsTrigger value="learning" className="text-xs">
                <Star className="h-3 w-3 mr-1" />
                Learning
              </TabsTrigger>
            </TabsList>

            <TabsContent value="document" className="mt-0">
              <div className="space-y-2">
                {[
                  {
                    name: "Vendor Extraction",
                    desc: "Extract vendor info from invoices",
                    icon: "🏢",
                    finma: "finma_recommended" as const,
                  },
                  {
                    name: "Document Classification",
                    desc: "Classify documents by type",
                    icon: "📄",
                    finma: "finma_optional" as const,
                  },
                  {
                    name: "Format Detection",
                    desc: "Detect file formats",
                    icon: "🔍",
                    finma: "finma_optional" as const,
                  },
                  {
                    name: "Entity Extraction",
                    desc: "Extract companies, amounts, dates",
                    icon: "📋",
                    finma: "finma_recommended" as const,
                  },
                ].map(process => (
                  <div
                    key={process.name}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                  >
                    <span className="text-lg">{process.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{process.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {process.desc}
                      </p>
                    </div>
                    <FinmaBadge classification={process.finma} />
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="accounting" className="mt-0">
              <div className="space-y-2">
                {[
                  {
                    name: "Transaction Categorization",
                    desc: "Categorize bank transactions",
                    icon: "💳",
                  },
                  {
                    name: "Booking Suggestions",
                    desc: "AI-driven double-entry bookings",
                    icon: "📊",
                  },
                  {
                    name: "VAT Decisions",
                    desc: "Determine VAT rates and treatment",
                    icon: "🧾",
                  },
                  {
                    name: "Tax Calculation Review",
                    desc: "Validate tax calculations",
                    icon: "📈",
                  },
                  {
                    name: "Reconciliation Matching",
                    desc: "Match transactions to documents",
                    icon: "🔗",
                  },
                  {
                    name: "Anomaly Detection",
                    desc: "Detect unusual patterns",
                    icon: "⚠️",
                  },
                ].map(process => (
                  <div
                    key={process.name}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                  >
                    <span className="text-lg">{process.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{process.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {process.desc}
                      </p>
                    </div>
                    <FinmaBadge classification="finma_required" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" />
                All accounting processes require Swiss data residency per FINMA
                guidelines
              </p>
            </TabsContent>

            <TabsContent value="analysis" className="mt-0">
              <div className="space-y-2">
                {[
                  {
                    name: "Smart Analysis",
                    desc: "Financial data insights",
                    icon: "🧠",
                    finma: "finma_optional" as const,
                  },
                  {
                    name: "Report Generation",
                    desc: "Generate financial reports",
                    icon: "📑",
                    finma: "finma_required" as const,
                  },
                  {
                    name: "Chart Data Analysis",
                    desc: "Analyze data for visualization",
                    icon: "📊",
                    finma: "finma_optional" as const,
                  },
                  {
                    name: "Trend Analysis",
                    desc: "Analyze financial trends",
                    icon: "📈",
                    finma: "finma_optional" as const,
                  },
                  {
                    name: "Compliance Check",
                    desc: "Swiss GAAP, tax law compliance",
                    icon: "✅",
                    finma: "finma_recommended" as const,
                  },
                ].map(process => (
                  <div
                    key={process.name}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                  >
                    <span className="text-lg">{process.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{process.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {process.desc}
                      </p>
                    </div>
                    <FinmaBadge classification={process.finma} />
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="learning" className="mt-0">
              <div className="space-y-2">
                {[
                  {
                    name: "Pre-Import Recommendations",
                    desc: "Suggest data cleanup",
                    icon: "🔧",
                  },
                  {
                    name: "Entity Tuning",
                    desc: "Improve entity recognition",
                    icon: "🎯",
                  },
                  {
                    name: "Pattern Learning",
                    desc: "Learn transaction patterns",
                    icon: "🔄",
                  },
                ].map(process => (
                  <div
                    key={process.name}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                  >
                    <span className="text-lg">{process.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{process.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {process.desc}
                      </p>
                    </div>
                    <FinmaBadge classification="finma_optional" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                <Star className="h-3 w-3" />
                Learning processes improve over time based on your corrections
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Get API Keys</CardTitle>
          <CardDescription>
            Visit these providers to create an account and get your API key
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(PROVIDER_INFO)
              .filter(([_, info]) => info.docsUrl)
              .map(([id, info]) => (
                <a
                  key={id}
                  href={info.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{info.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {info.description}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </a>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? "Edit Provider" : "Add AI Provider"}
            </DialogTitle>
            <DialogDescription>
              {editingProvider
                ? "Update your provider configuration. Leave API key blank to keep existing."
                : "Configure a new AI provider with your API key and usage settings."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Settings */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Key className="h-4 w-4" />
                Basic Configuration
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Display Name</Label>
                  <Input
                    id="name"
                    placeholder="My OpenAI Key"
                    value={formData.name}
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="provider">Provider</Label>
                  <Select
                    value={formData.providerId}
                    onValueChange={value => {
                      let providerType = value;
                      let dataResidency = formData.dataResidency;
                      let selectedModel = "";

                      // Set provider type and defaults based on selection
                      if (value === "vertex-builtin") {
                        providerType = "google-vertex";
                        dataResidency = "swiss";
                        selectedModel = "gemini-2.5-flash";
                      } else if (value === "claude-subscription") {
                        providerType = "anthropic";
                        dataResidency = "global";
                        selectedModel = "claude-3-5-sonnet-20241022";
                      } else if (["groq", "cerebras"].includes(value)) {
                        providerType = "openai-compat";
                        selectedModel = "llama-3.3-70b-versatile";
                      } else if (value === "anthropic") {
                        providerType = "anthropic";
                        selectedModel = "claude-3-5-sonnet-20241022";
                      } else if (value === "openai") {
                        providerType = "openai";
                        selectedModel = "gpt-4o";
                      } else if (value === "gemini") {
                        providerType = "google";
                        selectedModel = "gemini-2.5-flash";
                      } else if (value === "zai") {
                        providerType = "openai-compat";
                        selectedModel = "zai-coder-v1";
                      } else if (value === "openrouter") {
                        providerType = "openrouter";
                        selectedModel = "anthropic/claude-3.5-sonnet";
                      } else if (value === "azure") {
                        providerType = "azure";
                        selectedModel = "gpt-4o";
                      } else if (value === "bedrock") {
                        providerType = "bedrock";
                        selectedModel =
                          "anthropic.claude-3-5-sonnet-20241022-v2:0";
                      } else if (value === "xai") {
                        providerType = "openai-compat";
                        selectedModel = "grok-2";
                      } else if (value === "huggingface") {
                        providerType = "openai-compat";
                        selectedModel = "mistralai/Mixtral-8x7B-Instruct-v0.1";
                      }

                      setFormData({
                        ...formData,
                        providerId: value,
                        providerType,
                        dataResidency,
                        selectedModel,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Built-in option first */}
                      <SelectItem
                        value="vertex-builtin"
                        className="border-b mb-1 pb-2"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            🇨🇭 Built-in Gemini (Swiss)
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            No API Key
                          </Badge>
                        </div>
                      </SelectItem>
                      {/* Claude subscription option */}
                      <SelectItem
                        value="claude-subscription"
                        className="border-b mb-1 pb-2"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            🔐 Claude Subscription
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            OAuth
                          </Badge>
                        </div>
                      </SelectItem>
                      {/* User API key providers */}
                      {constants?.inferenceProviders.map(p => {
                        const info = getProviderInfo(p);
                        return (
                          <SelectItem key={p} value={p}>
                            <div className="flex items-center gap-2">
                              <span>{info.name}</span>
                              {info.swissAvailable && (
                                <span className="text-xs">🇨🇭</span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Built-in provider info banner */}
              {isBuiltInProvider && (
                <div className="p-3 bg-success/10 border border-[var(--success)]/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">No API key required</p>
                      <p className="text-xs text-muted-foreground">
                        This provider uses Firebase's built-in Vertex AI service
                        in Zurich (europe-west6). Your data stays in
                        Switzerland.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Claude subscription info banner */}
              {isSubscriptionProvider && (
                <div className="p-3 bg-info/10 border border-[var(--info)]/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Key className="h-5 w-5 text-info flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">
                        Claude Subscription Authentication
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Uses your Claude Pro/Team subscription via OAuth. You'll
                        be prompted to sign in with your Anthropic account when
                        using this provider.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Model selection - show if provider has models */}
              {availableModels.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="model">Model</Label>
                  <Select
                    value={formData.selectedModel || availableModels[0]?.id}
                    onValueChange={value =>
                      setFormData({ ...formData, selectedModel: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* API Key - only show for providers that require it */}
              {requiresApiKey && (
                <div className="space-y-2">
                  <Label htmlFor="apiKey">
                    API Key{" "}
                    {editingProvider && (
                      <span className="text-muted-foreground">
                        (leave blank to keep existing)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="apiKey"
                    type="password"
                    placeholder={editingProvider ? "••••••••" : "sk-..."}
                    value={formData.apiKey}
                    onChange={e =>
                      setFormData({ ...formData, apiKey: e.target.value })
                    }
                  />
                </div>
              )}

              {requiresApiKey &&
                ["openai-compat", "azure", "bedrock", "google-vertex"].includes(
                  formData.providerType
                ) && (
                  <div className="space-y-2">
                    <Label htmlFor="endpoint">API Endpoint (optional)</Label>
                    <Input
                      id="endpoint"
                      placeholder="https://api.example.com/v1"
                      value={formData.apiEndpoint || ""}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          apiEndpoint: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
            </div>

            <Separator />

            {/* Usage Category & Residency */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                Usage & Data Residency
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Usage Category</Label>
                  <Select
                    value={formData.usageCategory}
                    onValueChange={(
                      value: ProviderFormData["usageCategory"]
                    ) => {
                      // Auto-set residency for private_data
                      const newResidency =
                        value === "private_data" &&
                        formData.dataResidency === "global"
                          ? "swiss"
                          : formData.dataResidency;
                      setFormData({
                        ...formData,
                        usageCategory: value,
                        dataResidency: newResidency,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            {info.icon}
                            <span>{info.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {CATEGORY_INFO[formData.usageCategory]?.description}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Data Residency</Label>
                  <Select
                    value={formData.dataResidency}
                    onValueChange={(value: ProviderFormData["dataResidency"]) =>
                      setFormData({ ...formData, dataResidency: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(RESIDENCY_INFO).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <span>{info.flag}</span>
                            <span>{info.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.usageCategory === "private_data" &&
                    formData.dataResidency === "global" && (
                      <p className="text-xs text-warning">
                        Warning: Global residency not recommended for private
                        data
                      </p>
                    )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="default"
                  checked={formData.isDefaultForCategory}
                  onCheckedChange={checked =>
                    setFormData({
                      ...formData,
                      isDefaultForCategory: checked === true,
                    })
                  }
                />
                <Label
                  htmlFor="default"
                  className="text-sm font-normal cursor-pointer"
                >
                  Set as default for{" "}
                  {CATEGORY_INFO[formData.usageCategory]?.name ||
                    "this category"}
                </Label>
              </div>
            </div>

            <Separator />

            {/* Capabilities */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Brain className="h-4 w-4" />
                Model Capabilities
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    key: "vision",
                    label: "Vision/Images",
                    icon: <Eye className="h-3 w-3" />,
                  },
                  {
                    key: "audio",
                    label: "Audio/Voice",
                    icon: <Mic className="h-3 w-3" />,
                  },
                  {
                    key: "functionCalling",
                    label: "Function Calling",
                    icon: <Settings2 className="h-3 w-3" />,
                  },
                  {
                    key: "streaming",
                    label: "Streaming",
                    icon: <Globe className="h-3 w-3" />,
                  },
                  {
                    key: "longContext",
                    label: "Long Context (100k+)",
                    icon: <Brain className="h-3 w-3" />,
                  },
                  {
                    key: "embedding",
                    label: "Embeddings",
                    icon: <Key className="h-3 w-3" />,
                  },
                ].map(({ key, label, icon }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={
                        formData.capabilities[
                          key as keyof typeof formData.capabilities
                        ] || false
                      }
                      onCheckedChange={checked =>
                        setFormData({
                          ...formData,
                          capabilities: {
                            ...formData.capabilities,
                            [key]: checked === true,
                          },
                        })
                      }
                    />
                    <Label
                      htmlFor={key}
                      className="text-sm font-normal cursor-pointer flex items-center gap-1"
                    >
                      {icon}
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Custom Instructions */}
            <div className="space-y-2">
              <Label htmlFor="instructions">
                Custom Instructions (optional)
              </Label>
              <Textarea
                id="instructions"
                placeholder="Add custom system instructions for this provider (e.g., language preferences, output format requirements)"
                value={formData.customInstructions || ""}
                onChange={e =>
                  setFormData({
                    ...formData,
                    customInstructions: e.target.value,
                  })
                }
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                These instructions will be prepended to system prompts when
                using this provider
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingProvider ? "Update" : "Add Provider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteConfirm !== null}
        onOpenChange={() => setDeleteConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Provider?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this API key configuration. AI
              features using this provider will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                deleteConfirm && deleteMutation.mutate({ id: deleteConfirm })
              }
            >
              {deleteMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
